import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import styles from './Contact.module.css';
import StepOne from './StepOne';
import StepTwo from './StepTwo';
import StepThree from './StepThree';
import StepFour from './StepFour';
import StepFive from './StepFive';
import StepSix from './StepSix';
import SubmissionConfirmation from './SubmissionConfirmation';

const ContactForm = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    projectDescription: '',
    deckChoice: '',
    customDrawing: null,
    railingType: '',
    mountingSurface: '',
    stairRailingRequired: false,
    stairRailingSide: '',
    phone: '',
    deckMeasurements: {
      A: '',
      B: '',
      C: '',
      D: '',
      stairA: '',
      stairB: '',
    },
  });
  const [errors, setErrors] = useState({});
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [submissionError, setSubmissionError] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [quoteAmount, setQuoteAmount] = useState(null);

  const calculateTotalSteps = () => {
    if (formData.deckChoice === 'custom') {
      return 4;
    } else if (formData.deckChoice === 'Deck 2' || formData.deckChoice === 'Deck 3') {
      return 5;
    } else {
      return 6;
    }
  };

  const totalSteps = calculateTotalSteps();
  const currentStep = Math.min(step, totalSteps);
  const progress = (currentStep / totalSteps) * 100;

  const validateStep = () => {
    const newErrors = {};
    if (step === 1) {
      if (!formData.name) newErrors.name = "Name is required";
      if (!formData.email) {
        newErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Email format is invalid";
      }
      if (!formData.projectDescription) newErrors.projectDescription = "Project description is required";
    }
    if (step === 2) {
      if (!formData.deckChoice) {
        newErrors.deckChoice = "Please select a deck";
      } else if (formData.deckChoice === 'custom' && !formData.customDrawing) {
        newErrors.customDrawing = "Please upload a file for your custom drawing";
      }
    }
    if (step === 3 && !formData.railingType) {
      newErrors.railingType = "Please select a railing type";
    }
    if (step === 4 && isSubmitted && !formData.mountingSurface) {
      newErrors.mountingSurface = "Please select a mounting surface";
    }
    if (step === 5 && formData.deckChoice !== 'Deck 2' && formData.deckChoice !== 'Deck 3' && formData.stairRailingRequired && !formData.stairRailingSide) {
      newErrors.stairRailingSide = "Please choose the railing side";
    }
    if (step === 6 && formData.deckChoice !== 'custom') {
      if (!formData.deckMeasurements.A) newErrors.deckMeasurementsA = "Measurement A is required";
      if (!formData.deckMeasurements.B) newErrors.deckMeasurementsB = "Measurement B is required";
      if (!formData.deckMeasurements.C) newErrors.deckMeasurementsC = "Measurement C is required";
      if ((formData.deckChoice === 'Deck 4' || formData.deckChoice === 'Deck 5' || formData.deckChoice === 'Deck 6') && !formData.deckMeasurements.D) {
        newErrors.deckMeasurementsD = "Measurement D is required";
      }
      if (formData.stairRailingRequired && !formData.deckMeasurements.stairA) {
        newErrors.deckMeasurementsStairA = "Stair measurement A is required";
      }
      if (formData.stairRailingRequired && !formData.deckMeasurements.stairB) {
        newErrors.deckMeasurementsStairB = "Stair measurement B is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    setIsSubmitted(false);
    if (validateStep()) {
      if (step === 4) {
        if (formData.deckChoice !== 'custom') {
          if (formData.deckChoice === 'Deck 2' || formData.deckChoice === 'Deck 3') {
            setStep(step + 2);
          } else {
            setStep(step + 1);
          }
        }
      } else {
        setStep(step + 1);
      }
    }
  };

  const handlePreviousStep = () => {
    if (step === 6 && (formData.deckChoice === 'Deck 2' || formData.deckChoice === 'Deck 3')) {
      setStep(step - 2);
    } else {
      setStep(step - 1);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'deckChoice' && value !== 'custom') {
      setFormData(prevData => ({
        ...prevData,
        [name]: value,
        customDrawing: null
      }));
    } else {
      setFormData(prevData => ({
        ...prevData,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleMeasurementChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      deckMeasurements: {
        ...prevData.deckMeasurements,
        [name]: value
      }
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file && file.size > 10 * 1024 * 1024) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        fileSize: 'File size exceeds the maximum limit of 10 MB.'
      }));
      setFormData((prevFormData) => ({
        ...prevFormData,
        customDrawing: null
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        fileSize: ''
      }));
      setFormData((prevFormData) => ({
        ...prevFormData,
        customDrawing: file
      }));
    }
  };

  const handleCheckboxChange = (e) => {
    setFormData({ ...formData, stairRailingRequired: e.target.checked });
  };

  const calculateQuote = () => {
    const glassMultiplier = parseFloat(import.meta.env.VITE_GLASS_MULTIPLIER);
    const picketMultiplier = parseFloat(import.meta.env.VITE_PICKET_MULTIPLIER);
    const stairGlassMultiplier = parseFloat(import.meta.env.VITE_STAIR_GLASS_MULTIPLIER);
    const stairPicketMultiplier = parseFloat(import.meta.env.VITE_STAIR_PICKET_MULTIPLIER);

    const deckMeasurements = Object.entries(formData.deckMeasurements)
      .filter(([key, value]) => !key.startsWith('stair') && value !== '')
      .map(([, value]) => parseFloat(value));
    const totalDeckMeasurement = deckMeasurements.reduce((sum, value) => sum + value, 0);
    const deckLinearFeet = totalDeckMeasurement / 12;
    const deckMultiplier = formData.railingType === 'Glass' ? glassMultiplier : picketMultiplier;
    const deckCost = deckLinearFeet * deckMultiplier;

    let stairCost = 0;
    if (formData.stairRailingRequired) {
      const stairA = parseFloat(formData.deckMeasurements.stairA);
      const stairB = parseFloat(formData.deckMeasurements.stairB);
      const stairDiagonal = Math.sqrt(stairA ** 2 + stairB ** 2) / 12;
      const stairSideMultiplier = formData.stairRailingSide === 'Both Sides' ? 2 : 1;
      const stairTypeMultiplier = formData.stairRailingType === 'Glass' ? stairGlassMultiplier : stairPicketMultiplier;

      stairCost = stairDiagonal * stairSideMultiplier * stairTypeMultiplier;
    }

    const totalCost = deckCost + stairCost;
    return totalCost;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    if (validateStep()) {
      try {
        setSubmissionStatus('submitting');
        setSubmissionError(null);

        const formDataToSend = new FormData();
        Object.keys(formData).forEach(key => {
          if (key === 'customDrawing' && formData[key]) {
            formDataToSend.append('customDrawing', formData[key], formData[key].name);
          } else if (key === 'deckMeasurements') {
            formDataToSend.append('deckMeasurements', JSON.stringify(formData[key]));
          } else {
            formDataToSend.append(key, formData[key]);
          }
        });

        const quoteAmount = calculateQuote();
        formDataToSend.append('quoteAmount', quoteAmount);

        const response = await axios.post(`${import.meta.env.VITE_API_URL}/api/bgrs/submit-form`, formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          withCredentials: true
        });

        if (response.data.success) {
          setSubmissionStatus('success');
          const quote = calculateQuote();
          setQuoteAmount(quote);
          setStep(7);
        } else {
          throw new Error(response.data.error || 'Form submission failed');
        }
      } catch (error) {
        setSubmissionStatus('error');

        let errorMessage = 'An unexpected error occurred. Please try again.';
        if (error.response) {
          errorMessage = error.response.data.error || `Submission failed with status ${error.response.status}`;
        }

        setSubmissionError(errorMessage);
      }
    }
  };

  return (
    <div className={styles.container} role="form" aria-labelledby="form-title">
      <Helmet>
        <title>Contact Us - BGRS - Builder Grade Railing System</title>
        <meta name="description" content="Obtain a quick quote or get in touch with us for your railing needs." />
      </Helmet>
      <h1 id="form-title">Get a Free Quote*</h1>
      <div className={styles.progressBarContainer}>
        <div className={styles.progressBar} style={{ width: `${progress}%` }} aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100" role="progressbar"></div>
      </div>
      <form onSubmit={handleSubmit}>
        {step === 1 && (
          <StepOne
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
          />
        )}
        {step === 2 && (
          <StepTwo
            formData={formData}
            handleInputChange={handleInputChange}
            handleFileChange={handleFileChange}
            errors={errors}
          />
        )}
        {step === 3 && (
          <StepThree
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
          />
        )}
        {step === 4 && (
          <StepFour
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
          />
        )}
        {step === 5 && formData.deckChoice !== 'Deck 2' && formData.deckChoice !== 'Deck 3' && (
          <StepFive
            formData={formData}
            handleInputChange={handleInputChange}
            handleCheckboxChange={handleCheckboxChange}
            errors={errors}
          />
        )}
        {step === 6 && formData.deckChoice !== 'custom' && (
          <StepSix
            formData={formData}
            handleMeasurementChange={handleMeasurementChange}
            errors={errors}
          />
        )}
        {step === 7 && (
          <SubmissionConfirmation
            status={submissionStatus}
            quoteAmount={quoteAmount}
            deckChoice={formData.deckChoice}
          />
        )}

        {submissionError && (
          <div className={styles.errorMessage} role="alert">
            {submissionError}
          </div>
        )}

        <div className={styles.buttonContainer}>
          {step > 1 && step < 7 && (
            <button
              type="button"
              onClick={handlePreviousStep}
              className={styles.quoteButton}
              aria-label="Go to previous step"
              onKeyDown={(e) => e.key === 'Enter' && handlePreviousStep()}
            >
              Previous
            </button>
          )}
          {step === 4 && formData.deckChoice === 'custom' ? (
            <button
              type="button"
              className={styles.quoteButton}
              aria-label="Submit custom drawing"
              onClick={handleSubmit} // Ensure this explicitly calls handleSubmit
            >
              Submit
            </button>
          ) : (
            step < 6 && (
              <button
                type="button"
                onClick={handleNextStep}
                className={styles.quoteButton}
                aria-label="Proceed to next step"
                onKeyDown={(e) => e.key === 'Enter' && handleNextStep()}
              >
                Next
              </button>
            )
          )}
          {step === 6 && (
            <button
              type="submit"
              className={styles.quoteButton}
              aria-label="Submit form"
              onClick={handleSubmit} // Explicitly call handleSubmit
            >
              Submit
            </button>
          )}
        </div>
      </form>

      <p className={styles.siteVisitNotice}>*If a site visit is required, a nominal fee may apply, which will be waived if the quote is accepted.</p>
    </div>
  );
};

export default ContactForm;